import React from 'react';
import { Coins, Send, ShieldCheck, Loader2 } from 'lucide-react';

interface TokenPricingItem {
  label: string;
  cost: number;
}

interface TokenRequestItem {
  id: string;
  requestedByUserId: number;
  requestedByName: string;
  requestedByEmail?: string;
  requestedTokens: number;
  remarks: string;
  status: string;
  createdAt: string;
}

interface TokenOperationsSectionProps {
  viewerRole: string;
  tokenBalance: number | null;
  tokenWalletLoading: boolean;
  tokenPricing: Record<string, TokenPricingItem>;
  requests: TokenRequestItem[];
  requestsLoading: boolean;
  requestAmount: string;
  requestRemarks: string;
  onRequestAmountChange: (value: string) => void;
  onRequestRemarksChange: (value: string) => void;
  onSubmitRequest: (tab?: string) => Promise<void>;
  onRefreshRequests: () => Promise<void>;
  onApproveRequest: (requestId: string, requestedByUserId: number, tokenAmount: number) => Promise<void>;
  approveInProgressId?: string | null;
}

const formatDate = (value: string) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const TokenOperationsSection: React.FC<TokenOperationsSectionProps> = ({
  viewerRole,
  tokenBalance,
  tokenWalletLoading,
  tokenPricing,
  requests,
  requestsLoading,
  requestAmount,
  requestRemarks,
  onRequestAmountChange,
  onRequestRemarksChange,
  onSubmitRequest,
  onRefreshRequests,
  onApproveRequest,
  approveInProgressId,
}) => {
  const tokenPresetOptions = ['50', '100', '150', '200', 'custom'] as const;
  const [selectedPreset, setSelectedPreset] = React.useState<(typeof tokenPresetOptions)[number]>('50');
  const normalizedViewerRole = String(viewerRole || '').toLowerCase();
  const isSystemAdmin = normalizedViewerRole === 'system_admin';

  React.useEffect(() => {
    if (!requestAmount) {
      onRequestAmountChange('50');
      setSelectedPreset('50');
    }
  }, [requestAmount, onRequestAmountChange]);

  React.useEffect(() => {
    if (!requestAmount) return;
    if (requestAmount === '50' || requestAmount === '100' || requestAmount === '150' || requestAmount === '200') {
      setSelectedPreset(requestAmount as '50' | '100' | '150' | '200');
      return;
    }
    setSelectedPreset('custom');
  }, [requestAmount]);

  const handlePresetChange = (value: (typeof tokenPresetOptions)[number]) => {
    setSelectedPreset(value);
    if (value !== 'custom') {
      onRequestAmountChange(value);
    } else {
      onRequestAmountChange('');
    }
  };

  const pricingRows = [
    {
      key: 'download_raw_data',
      title: 'Download Raw Data',
      cost: tokenPricing.download_raw_data?.cost ?? 1,
    },
    {
      key: 'generate_alerts',
      title: 'Generate Alerts',
      cost: tokenPricing.generate_alerts?.cost ?? 1,
    },
    {
      key: 'run_rtc_optimization',
      title: 'Run RTC Optimization',
      cost: tokenPricing.run_rtc_optimization?.cost ?? 2,
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 min-h-[calc(100vh-180px)]">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-amber-500" />
            <h3 className="text-sm font-black text-gray-900" style={{ fontWeight: 1000 }}>
              Simulation-On-Demand Data Operations
            </h3>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between">
            <span className="text-xs font-black text-amber-900 uppercase tracking-wide">Available Tokens</span>
            <span className="text-lg font-black text-amber-900">
              {tokenWalletLoading ? 'Loading...' : (isSystemAdmin ? 'Unlimited (∞)' : `${tokenBalance ?? 0}`)}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {pricingRows.map((row) => {
              const available = isSystemAdmin || (tokenBalance ?? 0) >= row.cost;
              return (
                <div key={row.key} className="rounded-lg border border-gray-200 p-3 bg-white">
                  <p className="text-[11px] font-black text-gray-900 uppercase">{row.title}</p>
                  <p className="text-[10px] text-gray-500 font-bold mt-1">Cost per run</p>
                  <p className="text-xl font-black text-gray-900 leading-tight">{row.cost}</p>
                  <p className={`text-[10px] font-black mt-1 ${available ? 'text-green-700' : 'text-red-600'}`}>
                    {available ? 'Ready to run' : 'Low balance'}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50 space-y-3">
            <h4 className="text-xs font-black text-gray-900 uppercase">Request Token Recharge</h4>
            {isSystemAdmin && (
              <p className="text-[11px] font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded-md px-2 py-1">
                Token selection is visible, but recharge requests can only be submitted by HO Admin users.
              </p>
            )}
              <div>
                <label className="text-[11px] font-bold text-gray-700">Select Token Count</label>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2">
                  {tokenPresetOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handlePresetChange(option)}
                      className={`rounded-lg border px-2 py-2 text-xs font-black uppercase ${selectedPreset === option ? 'border-amber-500 bg-amber-500 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'}`}
                    >
                      {option === 'custom' ? 'Custom' : option}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-700">Requested Tokens</label>
                <input
                  type="number"
                  min={1}
                  value={requestAmount}
                  onChange={(e) => onRequestAmountChange(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="e.g. 50"
                  disabled={selectedPreset !== 'custom'}
                />
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-700">Remarks</label>
                <textarea
                  value={requestRemarks}
                  onChange={(e) => onRequestRemarksChange(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  rows={3}
                  placeholder="Why tokens are required"
                />
              </div>
              <button
                onClick={() => onSubmitRequest(undefined)}
                disabled={isSystemAdmin}
                className={`w-full rounded-lg text-white text-sm font-black py-2.5 flex items-center justify-center gap-2 ${isSystemAdmin ? 'bg-gray-300 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-600'}`}
              >
                <Send size={16} />
                {isSystemAdmin ? 'HO ADMIN CAN SUBMIT REQUEST' : 'Send Recharge Request'}
              </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 className="text-sm font-black text-gray-900" style={{ fontWeight: 1000 }}>
            Token Recharge Requests
          </h3>
          <button onClick={onRefreshRequests} className="text-xs font-black text-amber-600 hover:underline">
            Refresh
          </button>
        </div>

        <div className="p-4 max-h-[calc(100vh-250px)] overflow-y-auto">
          {requestsLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <p className="text-sm text-gray-500 font-bold text-center py-10">No token requests found.</p>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => {
                const isPending = String(req.status || '').toLowerCase() === 'pending';
                const canApprove = viewerRole === 'system_admin' && isPending;
                return (
                  <div key={req.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-black text-gray-900">{req.requestedByName}</p>
                        <p className="text-[11px] text-gray-500 font-bold">{req.requestedByEmail || 'N/A'}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-1 rounded-full font-black uppercase ${isPending ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'}`}>
                        {req.status}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-black text-gray-900">Requested: {req.requestedTokens} tokens</p>
                    <p className="text-xs text-gray-600 mt-1">{req.remarks || 'No remarks'}</p>
                    <p className="text-[10px] text-gray-400 mt-2">{formatDate(req.createdAt)}</p>

                    {canApprove && (
                      <button
                        onClick={() => onApproveRequest(req.id, req.requestedByUserId, req.requestedTokens)}
                        disabled={approveInProgressId === req.id}
                        className="mt-3 w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white text-xs font-black py-2 flex items-center justify-center gap-2"
                      >
                        {approveInProgressId === req.id ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                        Approve and Assign
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenOperationsSection;
